import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import { getToken, clearAuth } from '@/utils/storage'
import * as api from '@/api/endpoints'

Vue.use(Router)

const router = new Router({
  mode: 'hash',
  routes: [
    { path: '/', redirect: '/login' },
    { path: '/login', name: 'login', component: () => import('@/views/login/LoginView.vue') },
    { path: '/account-activate', name: 'activate', component: () => import('@/views/login/ActivateView.vue') },
    { path: '/password-forget', name: 'forget', component: () => import('@/views/login/ForgetView.vue') },
    { path: '/password-reset', name: 'reset', component: () => import('@/views/login/ResetView.vue') },
    { path: '/change-email', name: 'change-email', component: () => import('@/views/login/ChangeEmailView.vue') },
    { path: '/privacy', name: 'privacy', component: () => import('@/views/privacy/PrivacyView.vue') },
    { path: '/product', name: 'product', component: () => import('@/views/product/ProductView.vue'), meta: { requiresAuth: true } },
    { path: '/report', redirect: '/product' },
    { path: '/resource', name: 'resource', component: () => import('@/views/resource/ResourceView.vue'), meta: { requiresAuth: true } },
    { path: '/position', name: 'position', component: () => import('@/views/position/PositionView.vue'), meta: { requiresAuth: true } },
    { path: '/me', name: 'me', meta: { requiresAuth: true } },
    { path: '/profile/ins', name: 'profile-ins', component: () => import('@/views/profile/InstitutionalProfile.vue'), meta: { requiresAuth: true } },
    { path: '/profile/ind', name: 'profile-ind', component: () => import('@/views/profile/IndividualProfile.vue'), meta: { requiresAuth: true } },
    { path: '/logout', name: 'logout' },
    { path: '*', redirect: '/login' }
  ]
})

// Event bus shared between router guards and global dialog components
export const eventBus = new Vue()

let statementResolver = null
let statementRejector = null

export function showStatementDialog(statement) {
  return new Promise((resolve, reject) => {
    statementResolver = resolve
    statementRejector = reject
    eventBus.$emit('show-statement-dialog', statement)
  })
}

// Listen for dialog results from StatementDialog component
eventBus.$on('statement-accepted', () => {
  if (statementResolver) {
    statementResolver()
    statementResolver = null
    statementRejector = null
  }
})

eventBus.$on('statement-rejected', () => {
  if (statementRejector) {
    statementRejector()
    statementResolver = null
    statementRejector = null
  }
})

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  const token = getToken()

  // Handle logout — clear auth and go to /login directly
  if (to.path === '/logout') {
    store.dispatch('auth/logout')
    store.dispatch('timer/stopTimers')
    next('/login')
    return
  }

  // No token + protected route -> redirect to login
  if (!token && to.meta.requiresAuth) {
    next('/login')
    return
  }

  // Has token + trying to access login -> redirect to product
  if (token && to.path === '/login') {
    next('/product')
    return
  }

  // Handle /me alias
  if (to.path === '/me' && token) {
    try {
      const meData = await store.dispatch('user/fetchMe')
      if (/^INDIVIDUAL/.test(meData.clientTypeCode)) {
        next('/profile/ind')
      } else {
        next('/profile/ins')
      }
    } catch (e) {
      clearAuth()
      store.commit('user/CLEAR_ME')
      // If interceptor already triggered /login redirect, abort to avoid competing
      next(getToken() ? '/login' : false)
    }
    return
  }

  // Auth route: fetch me data and check guards
  if (token && to.meta.requiresAuth && to.path !== '/me') {
    try {
      const meData = await store.dispatch('user/fetchMe')

      // Check password expiry - block navigation until password changed
      if (meData.passwordExpired) {
        try {
          await new Promise((resolveCp, rejectCp) => {
            const handler = (result) => {
              eventBus.$off('change-password-done', onDone)
              eventBus.$off('change-password-failed', onFail)
              if (result && result.success) resolveCp()
              else rejectCp()
            }
            const onDone = handler
            const onFail = () => {
              eventBus.$off('change-password-done', onDone)
              eventBus.$off('change-password-failed', onFail)
              rejectCp()
            }
            eventBus.$on('change-password-done', onDone)
            eventBus.$on('change-password-failed', onFail)
            eventBus.$emit('show-change-password-dialog', { forced: true })
          })
          // Password changed, allow navigation to proceed
          store.commit('user/CLEAR_PASSWORD_EXPIRED')
          next()
        } catch (e) {
          clearAuth()
          store.commit('user/CLEAR_ME')
          next(getToken() ? '/login' : false)
        }
        return
      }

      // Check first-time statement
      if (meData.firstTimeStatementId) {
        try {
          const statement = await api.getStatement(meData.firstTimeStatementId)
          await showStatementDialog({ ...statement, mode: 'onetime', id: meData.firstTimeStatementId })
          await api.acceptStatement(meData.firstTimeStatementId)
          store.commit('user/MARK_STATEMENT_ACCEPTED', meData.firstTimeStatementId)
          // Statement accepted, allow navigation
          next()
        } catch (e) {
          // Statement rejected/error — go to /login directly
          clearAuth()
          store.commit('user/CLEAR_ME')
          next(getToken() ? '/login' : false)
        }
        return
      }

      // Check yearly statement
      if (meData.yearlyStatementId) {
        try {
          const statement = await api.getStatement(meData.yearlyStatementId)
          await showStatementDialog({ ...statement, mode: 'yearly', id: meData.yearlyStatementId })
          await api.acceptStatement(meData.yearlyStatementId)
          store.commit('user/MARK_STATEMENT_ACCEPTED', meData.yearlyStatementId)
          // Statement accepted, allow navigation
          next()
        } catch (e) {
          // Statement rejected/error — go to /login directly
          clearAuth()
          store.commit('user/CLEAR_ME')
          next(getToken() ? '/login' : false)
        }
        return
      }

      // All auth checks passed — start timers and allow navigation
      store.dispatch('timer/startTimers')
      next()

    } catch (e) {
      clearAuth()
      store.commit('user/CLEAR_ME')
      // If interceptor already triggered /login redirect, abort to avoid competing
      next(getToken() ? '/login' : false)
      return
    }
  }

  next()
})

// Suppress uncaught redirect errors caused by rapid navigation
const REDIRECT_RE = /Redirected when going from ".+" to ".+" via a navigation guard/i

router.onError((err) => {
  if (err instanceof Error && REDIRECT_RE.test(err.message)) {
    return
  }
  throw err
})

// $router.push/replace promises also reject on redirect — suppress them globally
const _push = Router.prototype.push
const _replace = Router.prototype.replace
Router.prototype.push = function push(location) {
  return _push.call(this, location).catch(err => {
    if (err instanceof Error && REDIRECT_RE.test(err.message)) return
    throw err
  })
}
Router.prototype.replace = function replace(location) {
  return _replace.call(this, location).catch(err => {
    if (err instanceof Error && REDIRECT_RE.test(err.message)) return
    throw err
  })
}

export default router
